import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * CustomerDashboard data hooks (ROADMAP Phase 2 tech-debt).
 *
 * The tables queried here (customers, enquiries, jobs, customer_message_threads,
 * customer_messages) are typed in the generated types, but the client is cast for
 * the same reason as the original code: the page casts supabase as
 * CustomerDashboardSupabase so that narrowly typed RPC + table helpers work
 * uniformly. We replicate that cast here to avoid introducing new type errors.
 *
 * The magic-link / auth flow that resolves `user` and `orgId` is handled by
 * AuthContext (onAuthStateChange) and OrgContext — those effects remain in the
 * component. All queries here are gated `enabled: Boolean(userId && orgId)` so
 * they don't fire until both are resolved.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type Customer = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  address: string | null;
};

export type Enquiry = {
  id: string;
  subject: string | null;
  additional_info: string | null;
  status: string;
  created_at: string;
};

export type Booking = {
  id: string;
  title: string;
  scheduled_date: string;
  start_time: string;
  status: string;
  customer_notes: string | null;
  created_at: string;
};

export type Thread = {
  id: string;
  customer_id: string;
  enquiry_id: string | null;
  booking_id: string | null;
  subject: string | null;
  created_at: string;
};

export type Message = {
  id: string;
  thread_id: string;
  sender_type: 'customer' | 'admin' | 'system';
  body: string;
  created_at: string;
};

export interface CustomerDashboardData {
  customers: Customer[];
  enquiries: Enquiry[];
  bookings: Booking[];
  threads: Thread[];
  messages: Message[];
}

/**
 * Loads all data for the authenticated customer dashboard in one query.
 *
 * Calls `claim_customer_accounts` first (associates anonymous records with the
 * signed-in user), then fetches customers → enquiries/bookings/threads in
 * parallel → messages for all threads.
 *
 * Gated on both `userId` and `orgId` so it never fires before the magic-link
 * session is fully resolved by AuthContext.
 */
export function useCustomerDashboardData(
  userId: string | undefined,
  orgId: string | undefined
) {
  return useQuery({
    queryKey: ['customer-dashboard', userId, orgId],
    enabled: Boolean(userId) && Boolean(orgId),
    queryFn: async (): Promise<CustomerDashboardData> => {
      await db.rpc('claim_customer_accounts');

      const { data: customerRows, error: customerError } = await db
        .from('customers')
        .select('id, name, email, phone, address')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });

      if (customerError) throw customerError;

      const customers = (customerRows || []) as Customer[];
      const customerIds = customers.map((c: Customer) => c.id);

      if (customerIds.length === 0) {
        return { customers, enquiries: [], bookings: [], threads: [], messages: [] };
      }

      const [{ data: enquiryRows }, { data: bookingRows }, { data: threadRows }] =
        await Promise.all([
          db
            .from('enquiries')
            .select('id, subject, additional_info, status, created_at')
            .eq('organization_id', orgId)
            .in('customer_id', customerIds)
            .order('created_at', { ascending: false }),
          db
            .from('jobs')
            .select('id, title, scheduled_date, start_time, status, customer_notes, created_at')
            .eq('organization_id', orgId)
            .in('customer_id', customerIds)
            .order('scheduled_date', { ascending: true }),
          db
            .from('customer_message_threads')
            .select('id, customer_id, enquiry_id, booking_id, subject, created_at')
            .eq('organization_id', orgId)
            .in('customer_id', customerIds)
            .order('created_at', { ascending: false }),
        ]);

      const threads = (threadRows || []) as Thread[];

      let messages: Message[] = [];
      if (threads.length > 0) {
        const { data: messageRows } = await db
          .from('customer_messages')
          .select('id, thread_id, sender_type, body, created_at')
          .eq('organization_id', orgId)
          .in(
            'thread_id',
            threads.map((t: Thread) => t.id)
          )
          .order('created_at', { ascending: true });
        messages = (messageRows || []) as Message[];
      }

      return {
        customers,
        enquiries: (enquiryRows || []) as Enquiry[],
        bookings: (bookingRows || []) as Booking[],
        threads,
        messages,
      };
    },
  });
}

// ---------------------------------------------------------------------------
// Write mutations
// ---------------------------------------------------------------------------

interface SendMessageInput {
  threadId: string;
  organizationId: string;
  customerId: string;
  senderUserId: string | undefined;
  body: string;
}

/** Send a customer reply on a message thread, then invalidate the dashboard. */
export function useSendCustomerMessage(userId: string | undefined, orgId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendMessageInput): Promise<void> => {
      const { error } = await db.from('customer_messages').insert({
        thread_id: input.threadId,
        organization_id: input.organizationId,
        customer_id: input.customerId,
        sender_type: 'customer',
        sender_user_id: input.senderUserId,
        body: input.body,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-dashboard', userId, orgId] });
    },
  });
}

interface SaveProfileInput {
  customerId: string;
  name: string;
  phone: string;
  address: string;
}

/** Update the customer's own profile via the `update_customer_profile` RPC. */
export function useSaveCustomerProfile(userId: string | undefined, orgId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveProfileInput): Promise<void> => {
      const { error } = await db.rpc('update_customer_profile', {
        _customer_id: input.customerId,
        _name: input.name,
        _phone: input.phone,
        _address: input.address,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-dashboard', userId, orgId] });
    },
  });
}

interface RescheduleBookingInput {
  bookingId: string;
  scheduledDate: string;
  startTime: string;
}

/** Reschedule a booking via the `reschedule_customer_booking` RPC. */
export function useRescheduleCustomerBooking(
  userId: string | undefined,
  orgId: string | undefined
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: RescheduleBookingInput): Promise<void> => {
      const { error } = await db.rpc('reschedule_customer_booking', {
        _booking_id: input.bookingId,
        _scheduled_date: input.scheduledDate,
        _start_time: input.startTime,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-dashboard', userId, orgId] });
    },
  });
}

interface CancelBookingInput {
  bookingId: string;
}

/** Cancel a booking via the `cancel_customer_booking` RPC. */
export function useCancelCustomerBooking(userId: string | undefined, orgId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CancelBookingInput): Promise<void> => {
      const { error } = await db.rpc('cancel_customer_booking', {
        _booking_id: input.bookingId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-dashboard', userId, orgId] });
    },
  });
}

interface CancelEnquiryInput {
  enquiryId: string;
}

/** Cancel an enquiry via the `cancel_customer_enquiry` RPC. */
export function useCancelCustomerEnquiry(userId: string | undefined, orgId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CancelEnquiryInput): Promise<void> => {
      const { error } = await db.rpc('cancel_customer_enquiry', {
        _enquiry_id: input.enquiryId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-dashboard', userId, orgId] });
    },
  });
}
